import { useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { HQGetFlexWorkerDetailsResponse } from '@types';
import { useUpdateFlexWorker } from 'hq/hooks/queryHooks/flexWorkers/useUpdateFlexWorker';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { LwButton, LwFormDate, LwFormNumberInput } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import { resolveContractMinutes } from '../../../../../../../shared/utils/resolve-contract-minutes';

type Props = {
  data: HQGetFlexWorkerDetailsResponse;
  onClose: () => void;
};

type Form = {
  contractStartDate: string | null;
  contractEndDate: string | null;
  contractHours: number | null;
};

export const FlexWorkerContractBody = (props: Props) => {
  const { t } = useTranslation(['all-platforms-common']);
  const { alertSuccess, alertError } = useAlert();
  const updateFlexWorker = useUpdateFlexWorker(props.data.id);
  const form = useForm<Form>({
    defaultValues: {
      contractStartDate: props.data.contractStartDate,
      contractEndDate: props.data.contractEndDate,
      contractHours: props.data.contractMinutes ? props.data.contractMinutes / 60 : 0,
    },
  });

  const handleSubmit = useCallback(() => {
    form.handleSubmit((data) => {
      const payload = {
        ...data,
        contractMinutes: resolveContractMinutes(data.contractHours ?? undefined),
      };
      updateFlexWorker.mutate(
        {
          contractStartDate: payload.contractStartDate,
          contractEndDate: payload.contractEndDate,
          contractMinutes: payload.contractMinutes,
        },
        {
          onSuccess: () => {
            props.onClose();
            alertSuccess('Contractdata opgeslagen');
          },
          onError: (e) => {
            alertError(e);
          },
        }
      );
    })();
  }, []);

  return (
    <Box
      padding={6}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
    >
      <Box>
        <Typography mb={2}>Stel de ingangs- en einddatum van het contract in</Typography>
        <Grid container spacing={4} xs={6}>
          <Grid item xs={6}>
            <LwFormDate
              control={form.control}
              name={'contractStartDate'}
              rules={{ required: 'Vul in wanneer het contract ingaat' }}
              label={t('worker_data.contract_start_date', { ns: 'all-platforms-common' })}
            />
          </Grid>
          <Grid item xs={6}>
            <LwFormDate
              control={form.control}
              name={'contractEndDate'}
              label={t('worker_data.contract_end_date', { ns: 'all-platforms-common' })}
            />
          </Grid>
          <Grid item xs={6}>
            <LwFormNumberInput
              control={form.control}
              name={'contractHours'}
              label={t('worker_data.contract_hours', { ns: 'all-platforms-common' })}
              rules={{ min: { value: 0, message: 'Contracturen kunnen niet negatief zijn' } }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box display="flex" flexDirection="row" justifyContent="center">
        <LwButton onClick={handleSubmit}>Indienen</LwButton>
        <Box ml={4}>
          <LwButton color="secondary" onClick={props.onClose}>
            Annuleren
          </LwButton>
        </Box>
      </Box>
    </Box>
  );
};
